import React, { useEffect, useCallback, useState, useRef, memo } from 'react';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSidebar } from '../../contexts/SidebarContext';
import MainLayout from '../layout/MainLayout';
import ContentArea from '../layout/ContentArea';
import Sidebar from '../layout/Sidebar';
import Button from '../common/button/Button';
import PillButton from '../common/button/PillButton';
import AgentLogo from '../common/logo/AgentLogo';
import AgentBadges from './AgentBadges';
import UnderlineTabMenu from '../common/menu/UnderlineTabMenu';
import AgentChatsList from './AgentChatsList';
import AgentSettings from './AgentSettings';
import Toast from '../common/Toast';
import { getAgentBySlug } from '../../api/agents/agents';
import PersonalMemoriesModal from '../settings/PersonalMemoriesModal';

import styles from './styles/AgentDetailsContainer.module.css';

const AgentDetailsContainer = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { setPage: setSidebarPage, isSidebarCollapsed } = useSidebar();
    const [agent, setAgent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);
    const [activeTab, setActiveTab] = useState('agent-chats'); // 'agent-chats' or 'my-chats'
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);
    const scrollRootRef = useRef(null);

    // Route-based settings view: /agents/:slug/settings
    const settingsMatch = useMatch('/agents/:slug/settings');
    const isSettingsRoute = !!settingsMatch;

    // Fetch agent details
    useEffect(() => {
        const fetchAgent = async () => {
            try {
                setLoading(true);
                const data = await getAgentBySlug(slug);
                setAgent(data);

                // If user is not owner and agent is private, redirect to agent chat
                if (!data.isOwner && data.accessType === 'PRIVATE') {
                    navigate(`/agent/${slug}`);
                    return;
                }
            } catch (err) {
                console.error('Error fetching agent details:', err);
                // If access denied (403/404), redirect to agent chat page
                if (err.message.includes('Failed to fetch agent') ||
                    err.message.includes('Access denied') ||
                    err.message.includes('Forbidden') ||
                    err.message.includes('Agent not found')) {
                    navigate(`/agent/${slug}`);
                    return;
                }
                setError(err.message || 'Failed to load agent');
            } finally {
                setLoading(false);
            }
        };

        fetchAgent();
    }, [slug, navigate]);

    useEffect(() => {
        if (agent?.name) {
            document.title = `JetChats - Agent ${agent.name}`;
        } else {
            document.title = 'JetChats - Agent';
        }
        setSidebarPage('agents');
    }, [setSidebarPage, agent]);

    // Enable transitions after initial load
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 50); // Shorter delay

        return () => clearTimeout(timer);
    }, []);

    const handleNewChat = useCallback(() => {
        if (agent?.slug) {
            navigate(`/agent/${agent.slug}`);
        } else {
            navigate('/agents');
        }
    }, [navigate, agent]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        navigate('/agents');
    }, [navigate]);

    // Toggle between settings and chats view
    const handleToggleSettings = useCallback(() => {
        if (!agent?.slug) return;
        if (isSettingsRoute) {
            navigate(`/agents/${agent.slug}`);
        } else {
            navigate(`/agents/${agent.slug}/settings`);
        }
    }, [agent, isSettingsRoute, navigate]);

    // Handle back from settings
    const handleBackFromSettings = useCallback(() => {
        if (agent?.slug) {
            navigate(`/agents/${agent.slug}`);
        } else {
            navigate('/agents');
        }
    }, [navigate, agent]);

    // Handle slug copy to clipboard
    const handleSlugCopy = useCallback(async () => {
        if (!agent?.slug) return;

        try {
            await navigator.clipboard.writeText(agent.slug);
            setToastMessage({ message: 'Slug copied to clipboard', type: 'info' });
        } catch (error) {
            console.error('Failed to copy slug:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = agent.slug;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setToastMessage({ message: 'Slug copied to clipboard', type: 'info' });
            } catch (fallbackError) {
                setToastMessage({ message: 'Failed to copy slug', type: 'error' });
            }
            document.body.removeChild(textArea);
        }
    }, [agent]);

    // Handle tab change
    const handleTabChange = useCallback((tabId) => {
        setActiveTab(tabId);
    }, []);

    // Tab menu configuration (only for owners)
    const tabs = agent?.isOwner ? [
        { id: 'agent-chats', label: 'Agent Chats' },
        { id: 'my-chats', label: 'My Chats' },
    ] : [];

    const renderContent = () => {
        if (loading) {
            return (
                <div className={styles.loaderContainer}>
                    <div>Loading...</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className={styles.errorContainer}>
                    <h2>Error</h2>
                    <p>{error}</p>
                    <Button onClick={handleBack} variant="secondary">
                        Back to Agents
                    </Button>
                </div>
            );
        }

        if (!agent) {
            return null;
        }

        if (isSettingsRoute) {
            return (
                <AgentSettings agent={agent} onBack={handleBackFromSettings} />
            );
        }

        const containerVariants = {
            hidden: { opacity: 0 },
            visible: {
                opacity: 1,
                transition: {
                    staggerChildren: 0.06,
                    delayChildren: 0.06,
                },
            },
        };

        return (
            <motion.div
                ref={scrollRootRef}
                className={`${styles.container} ${isSidebarCollapsed ? styles.containerCollapsed : ''} ${isInitialLoad ? styles.noTransition : ''}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <motion.div
                    className={styles.header}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        duration: 0.18,
                        ease: "easeOut"
                    }}
                    style={{ transition: 'none' }}
                >
                    <Button onClick={handleBack} variant="secondary" size="small">
                        ← Back to Agents
                    </Button>
                </motion.div>

                <div className={styles.content}>
                    <motion.div
                        className={styles.agentHeader}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.05 }}
                    >
                        <div className={styles.agentInfo}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.22, delay: 0.1 }}
                            >
                                <AgentLogo
                                    logo={agent.logo}
                                    name={agent.name}
                                    size="xlarge"
                                    className={styles.agentLogo}
                                />
                            </motion.div>
                            <motion.div
                                className={styles.agentDetails}
                                initial={{ opacity: 0, x: -15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.22, delay: 0.12 }}
                            >
                                <div className={styles.nameRow}>
                                    <h1 className={styles.agentName}>{agent.name}</h1>
                                    <AgentBadges isActive={agent.isActive} accessType={agent.accessType} />
                                </div>
                                <p className={styles.agentSlug} onClick={handleSlugCopy}>@{agent.slug}</p>
                                {agent.description && (
                                    <p className={styles.agentDescription}>
                                        {agent.description}
                                    </p>
                                )}
                            </motion.div>
                        </div>

                        <motion.div
                            className={styles.actions}
                            initial={{ opacity: 0, x: 15 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.22, delay: 0.14 }}
                        >
                            <PillButton variant="primary" onClick={handleNewChat}>
                                New Chat
                            </PillButton>
                            {agent?.isOwner && (
                                <PillButton variant="secondary" onClick={handleToggleSettings}>
                                    Configure Agent
                                </PillButton>
                            )}
                        </motion.div>
                    </motion.div>

                    {agent?.isOwner && (
                        <motion.div
                            className={styles.tabMenuContainer}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.22, delay: 0.18 }}
                        >
                            <UnderlineTabMenu
                                tabs={tabs}
                                activeTab={activeTab}
                                onTabChange={handleTabChange}
                            />
                        </motion.div>
                    )}

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.22 }}
                    >
                        {agent?.memoryEnabled && (activeTab === 'my-chats' || !agent.isOwner) && (
                            <div style={{ marginBottom: '1rem' }}>
                                <PillButton variant="secondary" onClick={() => setIsMemoriesOpen(true)}>
                                    View Personal Memories
                                </PillButton>
                            </div>
                        )}
                        <AgentChatsList
                            agentSlug={agent.slug}
                            chatType={agent.isOwner ? activeTab : 'my-chats'}
                            isOwner={agent.isOwner}
                            scrollRootRef={scrollRootRef}
                        />
                    </motion.div>
                </div>
            </motion.div>
        );
    };

    return (
        <MainLayout>
            <Sidebar handleNewChat={handleNewChat} newChatType="agent" />
            <ContentArea>
                {renderContent()}
            </ContentArea>

            <PersonalMemoriesModal
                isOpen={isMemoriesOpen}
                onClose={() => setIsMemoriesOpen(false)}
                agentId={agent?.id || null}
            />

            {toastMessage && (
                <Toast
                    message={toastMessage.message}
                    type={toastMessage.type}
                    onClose={() => setToastMessage(null)}
                />
            )}
        </MainLayout>
    );
};

export default memo(AgentDetailsContainer);
